/**
 * React binding for the push-permission state machine (M4).
 *
 * All the decision logic lives in `pushState.ts` and is pure; this file is
 * only about *when* to look and how a component gets at the result. That
 * matters more than it sounds: the state this hook exists to catch —
 * `lapsed` — changes while the app is closed, and nothing fires an event for
 * it. Re-reading at the right moments IS the feature.
 *
 * This is also where the subscribe step lives — `pushSubscribe.ts`'s
 * `subscribeAndPersist` / `reconcileOnStart`, called from here rather than
 * from the widget, so every consumer of this hook gets a working journey for
 * free. The identity these calls persist against is `auth.uid()`
 * (== `portal_users.id`, the value RLS checks `push_subscriptions.portal_user_id`
 * against — see `db/migrations/…`), read directly off the current Supabase
 * session rather than threaded through React context, so this hook has no
 * dependency on `AuthProvider` being mounted — same reason `readEnvironment`
 * and `requestBrowserPermission` are plain functions, not context reads.
 *
 * Authority: `.claude/skills/notifications/SKILL.md`,
 * `docs/handoff-notifications-milestone.md` §M4.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { isSubscriptionMine } from '../api/pushSubscriptions.ts';
import { getSupabaseClient } from '../api/supabase.ts';
import {
  claimDeviceForCurrentUser,
  reconcileOnStart,
  subscribeAndPersist,
  type SubscribeOutcome,
} from './pushSubscribe.ts';
import {
  canRequestPermission,
  canSelfHeal,
  derivePushState,
  readPushEnvironment,
  type PushEnvironment,
  type PushState,
} from './pushState.ts';
import { runtimeEnv } from '../runtimeConfig.ts';

export interface PushStatus {
  /** What to show right now. */
  state: PushState;
  /** True until the first environment read resolves — render nothing rather than flash a wrong state. */
  loading: boolean;
  /** Re-read the browser environment. Safe to call at any time; never triggers a permission prompt. */
  refresh: () => Promise<void>;
  /**
   * Ask the browser for notification permission, then — if it was granted —
   * actually subscribe and persist the row, then re-read and return the
   * resulting state. A successful journey ends in `on`, not `lapsed`. Only
   * call this from a real user gesture (a click handler), and only render
   * the button that calls it when `canRequestPermission(state)` is true —
   * see the guard inside for why this is enforced twice.
   */
  requestPermission: () => Promise<PushState>;
  /**
   * Re-subscribes WITHOUT prompting — the fix for `lapsed`. Permission is
   * already granted in that state, so no prompt is needed or possible; this
   * never calls `requestBrowserPermission`. Only render the button that
   * calls this when `canSelfHeal(state)` is true; it is a no-op (re-checked
   * against a fresh read, same reasoning as `requestPermission`) from any
   * other state.
   */
  selfHeal: () => Promise<PushState>;
  /**
   * Set when the most recent subscribe attempt (from `requestPermission` or
   * `selfHeal`) failed — a bad VAPID key, the browser refusing, a network
   * failure, or the write itself being rejected. Never carries the
   * subscription's `endpoint`/keys (personal data). Cleared at the start of
   * the next attempt.
   */
  subscribeError: string | null;
}

/** Production default: the identity `subscribe`/`reconcile` persist against, read off the live session. */
async function defaultGetPortalUserId(): Promise<string | null> {
  try {
    const { data, error } = await getSupabaseClient().auth.getUser();
    if (error || !data.user) return null;
    return data.user.id;
  } catch {
    return null;
  }
}

/** Production default: the real subscribe-and-persist step. */
async function defaultSubscribe(portalUserId: string): Promise<SubscribeOutcome> {
  try {
    const vapidPublicKey = runtimeEnv('VITE_VAPID_PUBLIC_KEY') ?? '';
    return await subscribeAndPersist(getSupabaseClient(), portalUserId, vapidPublicKey);
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'push subscribe failed' };
  }
}

/** Production default: the real on-start reconcile step. */
async function defaultReconcile(portalUserId: string): Promise<void> {
  try {
    await reconcileOnStart(getSupabaseClient(), portalUserId);
  } catch {
    // best-effort — see reconcileOnStart's own doc.
  }
}

/** Production default: take the device over from a previous account. */
async function defaultClaimDevice(portalUserId: string): Promise<SubscribeOutcome> {
  try {
    const vapidPublicKey = runtimeEnv('VITE_VAPID_PUBLIC_KEY') ?? '';
    return await claimDeviceForCurrentUser(getSupabaseClient(), portalUserId, vapidPublicKey);
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'push claim failed' };
  }
}

/** Production default: ask the database whose subscription this endpoint is. */
async function defaultCheckOwnership(endpoint: string): Promise<boolean | null> {
  try {
    return await isSubscriptionMine(getSupabaseClient(), endpoint);
  } catch {
    return null;
  }
}

/** Injection point for tests; production passes nothing and hits the real browser APIs. */
export interface UsePushStatusOptions {
  readEnvironment?: () => Promise<PushEnvironment>;
  requestBrowserPermission?: () => Promise<NotificationPermission>;
  /**
   * Resolves the signed-in customer's id. Production default reads it off
   * the current Supabase session; returns null (skipping subscribe/reconcile
   * entirely) when there is no session or the read fails. Test injection
   * point so tests don't need a real session or a configured Supabase
   * client.
   */
  getPortalUserId?: () => Promise<string | null>;
  /** Test injection for the subscribe-and-persist step; production default performs the real subscribe + upsert. */
  subscribe?: (portalUserId: string) => Promise<SubscribeOutcome>;
  /** Test injection for the on-start reconcile step; production default performs the real reconcile. */
  reconcile?: (portalUserId: string) => Promise<void>;
  /**
   * Test injection for the device-takeover step (`foreign`); production default
   * unsubscribes the previous account's browser subscription and mints a fresh
   * one. See `claimDeviceForCurrentUser`.
   */
  claimDevice?: (portalUserId: string) => Promise<SubscribeOutcome>;
  /**
   * Does the browser's current subscription belong to the signed-in account?
   * Production default asks the database (RLS answers it). Returning null means
   * "undetermined", which `derivePushState` reads as "assume it is theirs".
   */
  checkOwnership?: (endpoint: string) => Promise<boolean | null>;
}

export function usePushStatus(options: UsePushStatusOptions = {}): PushStatus {
  const readRawEnvironment = options.readEnvironment ?? readPushEnvironment;
  const requestBrowserPermission =
    options.requestBrowserPermission ?? (() => Notification.requestPermission());
  const getPortalUserId = options.getPortalUserId ?? defaultGetPortalUserId;
  const subscribe = options.subscribe ?? defaultSubscribe;
  const reconcile = options.reconcile ?? defaultReconcile;
  const claimDevice = options.claimDevice ?? defaultClaimDevice;
  const checkOwnership = options.checkOwnership ?? defaultCheckOwnership;

  /**
   * The environment read EVERY decision in this hook goes through.
   *
   * `readPushEnvironment` can only report device facts, and the shared-device
   * bug was precisely that device facts were treated as the whole answer. The
   * ownership lookup is grafted on here — in one place — so no call site can
   * accidentally decide from the raw, account-blind read.
   *
   * Skipped when there is no subscription to attribute (nothing to ask about,
   * and it saves a query on the common `off` / `lapsed` paths) and when the
   * reader ALREADY determined ownership — `null` means undetermined, so a
   * reader that states an answer is authoritative and is never overridden here.
   * That keeps `readEnvironment` a complete injection point for tests instead
   * of one this wrapper silently overrules.
   */
  const readEnvironment = useCallback(async (): Promise<PushEnvironment> => {
    const env = await readRawEnvironment();
    if (!env.hasSubscription || !env.endpoint || env.subscriptionIsMine !== null) {
      return env;
    }

    return { ...env, subscriptionIsMine: await checkOwnership(env.endpoint) };
  }, [readRawEnvironment, checkOwnership]);

  const [state, setState] = useState<PushState>('not_ready');
  const [loading, setLoading] = useState(true);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);

  // Guards a setState after unmount — reads here are async and can be
  // in flight when navigation unmounts the widget mid-read.
  const mounted = useRef(true);

  // Reconcile is an app-start correction, not a per-wake one: this guards it
  // to at most once per mount. Only flips once `getPortalUserId` actually
  // resolved to something, so a wake before the session is ready can still
  // retry on the next one, rather than being permanently skipped.
  const reconciledRef = useRef(false);

  const refresh = useCallback(async () => {
    const env = await readEnvironment();
    if (!mounted.current) return;
    const next = derivePushState(env);
    setState(next);
    setLoading(false);

    if (next === 'on' && !reconciledRef.current) {
      const portalUserId = await getPortalUserId();
      if (portalUserId) {
        reconciledRef.current = true;
        void reconcile(portalUserId);
      }
    }
  }, [readEnvironment, getPortalUserId, reconcile]);

  useEffect(() => {
    mounted.current = true;
    void refresh();

    /**
     * Re-read when the app comes back to the foreground.
     *
     * This is the entire mechanism behind detecting `lapsed`. A subscription
     * dies while the app is CLOSED — iOS prunes them after inactivity, and
     * browsers rotate endpoints silently — so nothing we could listen to ever
     * fires. The next time the customer opens the app is the first moment we
     * can notice, so that is when we look.
     *
     * Both events, not one: `visibilitychange` covers a background tab
     * becoming visible without necessarily taking window focus;
     * `focus` covers window managers/OSes that refocus the window without
     * changing document visibility. Either can fire without the other. The
     * two handlers overlap harmlessly — the read is idempotent and cheap.
     */
    const onWake = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void refresh();
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onWake);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onWake);
    }

    return () => {
      mounted.current = false;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onWake);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onWake);
      }
    };
  }, [refresh]);

  const requestPermission = useCallback(async (): Promise<PushState> => {
    /**
     * ⚠️ THE GUARD THAT PROTECTS THE ONE-SHOT.
     *
     * `Notification.requestPermission()` can be spent exactly once per
     * origin: once resolved 'denied', every future call resolves 'denied'
     * immediately without prompting, and clearing site data does not reset
     * it — the grant lives in the browser's per-origin settings, not in our
     * app. Calling it when no service worker is registered asks the
     * question with no way to act on a 'granted' answer: no subscription can
     * follow, so the shot is spent for nothing and every later, working
     * build is locked out of ever asking again.
     *
     * `derivePushState` already ranks `not_ready`, `blocked`, `unsupported`
     * and `needs_install` above `off`, so a correctly rendered widget never
     * shows the button outside `off`. This re-checks against a fresh read
     * anyway, rather than trusting the possibly-stale `state` in scope: the
     * cost is one more environment read, and the failure it prevents is
     * unrecoverable.
     */
    const env = await readEnvironment();
    const current = derivePushState(env);

    if (!canRequestPermission(current)) {
      if (mounted.current) setState(current);
      return current;
    }

    if (mounted.current) setSubscribeError(null);
    await requestBrowserPermission();

    // Re-read rather than trusting the resolved permission value: 'granted'
    // does not imply subscribed, and `on` versus `lapsed` is decided by the
    // subscription, not the permission.
    const after = await readEnvironment();

    // The subscribe step: without this, a grant ends the journey at
    // `lapsed` — `permission: 'granted'` + `hasSubscription: false` — the
    // "avisos pararam de funcionar" screen, on a device that never worked in
    // the first place. Only attempted when the prompt actually resulted in
    // 'granted' (the customer may have dismissed or denied it) and there is
    // someone to persist the row for.
    if (after.permission === 'granted') {
      const portalUserId = await getPortalUserId();
      if (portalUserId) {
        const outcome = await subscribe(portalUserId);
        if (!outcome.ok && mounted.current) {
          setSubscribeError(outcome.error);
        }
      }
    }

    // Re-read again rather than assuming the subscribe attempt's outcome:
    // `on` versus `lapsed` must reflect what the browser actually holds now,
    // not what we hoped just happened.
    const final = await readEnvironment();
    const next = derivePushState(final);
    if (mounted.current) setState(next);
    return next;
  }, [readEnvironment, requestBrowserPermission, getPortalUserId, subscribe]);

  const selfHeal = useCallback(async (): Promise<PushState> => {
    // Same fresh-read guard as `requestPermission`, and for the same reason:
    // never trust the possibly-stale `state` in scope. This additionally
    // guarantees `requestBrowserPermission` is never reachable from here —
    // it is not even in this function's dependency list.
    const env = await readEnvironment();
    const current = derivePushState(env);

    if (!canSelfHeal(current)) {
      if (mounted.current) setState(current);
      return current;
    }

    if (mounted.current) setSubscribeError(null);

    const portalUserId = await getPortalUserId();
    if (portalUserId) {
      // `foreign` needs a DIFFERENT repair from `lapsed`, and using the wrong
      // one is silent: a plain subscribe re-persists the SAME endpoint, which
      // still belongs to the previous account, so the upsert is refused by RLS
      // and the customer stays exactly as unreachable as before — with no
      // error to show for it. The takeover mints a new endpoint instead.
      const outcome = current === 'foreign'
        ? await claimDevice(portalUserId)
        : await subscribe(portalUserId);
      if (!outcome.ok && mounted.current) {
        setSubscribeError(outcome.error);
      }
    }

    const after = await readEnvironment();
    const next = derivePushState(after);
    if (mounted.current) setState(next);
    return next;
  }, [readEnvironment, getPortalUserId, subscribe, claimDevice]);

  return { state, loading, refresh, requestPermission, selfHeal, subscribeError };
}
